// Home, the landing page (redesign slice 3; wireframes 1, 13 and 23, styled
// as board 28). It answers "what's next in my kitchen?": today's and
// tomorrow's meals, one grocery line, boxes and market days from starred
// sources, and recipes newly added to the library. Everything is worked out
// in src/lib/homeUtils.ts from data the app already loads.
//
// Boxes and markets are shown to app admins only until the purveyor pages
// are released to everyone (decided 2026-10-03); the section's data loads
// only for them.
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useGroceryState } from "../hooks/useGroceryState";
import { weekLines } from "../lib/groceryView";
import { extrasForWeek, progress } from "../lib/groceryState";
import { DrawnIcon } from "../components/NavIcons";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import { formatMinutes, shiftWeek, toISODate } from "../lib/utils";
import { loadHouseholdSources, loadPurveyorDirectory } from "../lib/db";
import {
  formatShortDate,
  formatTime,
  homeDays,
  isoToDate,
  mealsForDay,
  newestRecipes,
  upcomingBoxes,
  upcomingMarketDays,
  weekHasMeals,
  type HomeDay,
  type PlannedMeal,
} from "../lib/homeUtils";
import type { Purveyor } from "../lib/types";

const SLOT_LABELS = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner" } as const;

// ------------------------------------------------------------ styles ---

const smallLabel: CSSProperties = { fontSize: FONT_SIZE.xs, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" };
const supporting: CSSProperties = { fontSize: FONT_SIZE.sm, color: COLORS.soft };
const sectionHeading: CSSProperties = { fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600, margin: "0 0 var(--space-md)", lineHeight: 1.2 };
const textAction: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: LAYOUT.tapTarget,
  color: COLORS.accent,
  fontSize: FONT_SIZE.base,
  fontWeight: 700,
  textDecoration: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
};
const primaryButton: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 52,
  padding: "0 var(--space-x3)",
  borderRadius: RADIUS.md,
  background: COLORS.accent,
  color: COLORS.onAccent,
  fontSize: FONT_SIZE.base,
  fontWeight: 700,
  textDecoration: "none",
  border: "none",
  cursor: "pointer",
};
const secondaryButton: CSSProperties = { ...primaryButton, background: "transparent", color: COLORS.accent, border: `1.5px solid ${COLORS.accent}` };

// ------------------------------------------------------------- page ---

export function HomePage({ isAdmin }: { isAdmin: boolean }) {
  const mp = useMealPlanner();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const days = useMemo(() => homeDays(new Date(), mp.planDefaults.weekStartDay), [mp.planDefaults.weekStartDay]);
  const thisWeek = days[0].weekStart;
  const { setWeekStart, weekStart } = mp;

  // Home is about now: point the shared "week being viewed" back at this
  // week, so the grocery line, "See the whole week" and "Start from last
  // week" all act on it, whatever week Plan was left on.
  useEffect(() => {
    if (weekStart !== thisWeek) setWeekStart(thisWeek);
  }, [weekStart, thisWeek, setWeekStart]);

  const planned = days.map((d) => ({ day: d, meals: mealsForDay(mp.plansByWeek[d.weekStart], d.day, mp.recipeById) }));
  const nothingPlanned = !weekHasMeals(mp.plansByWeek[thisWeek]) && planned.every((p) => p.meals.length === 0);
  // The same saved, shared state as the Groceries tab (slice 6).
  const grocery = useGroceryState(mp.householdId, thisWeek, mp.reloadWeekPlans);
  const groceryLines = useMemo(() => weekLines(mp.plansByWeek, thisWeek, mp.recipeById).lines, [mp.plansByWeek, thisWeek, mp.recipeById]);
  const groceries = progress(groceryLines, extrasForWeek(grocery.extras, thisWeek), grocery.marks);
  const lastWeek = shiftWeek(thisWeek, -1);
  const canCopyLastWeek = weekHasMeals(mp.plansByWeek[lastWeek]);

  function startFromLastWeek() {
    mp.applyWeekAsTemplate(lastWeek);
    navigate("/plan");
  }

  const left = nothingPlanned ? (
    <EmptyWeek date={days[0].date} canCopyLastWeek={canCopyLastWeek && weekStart === thisWeek} onCopyLastWeek={startFromLastWeek} />
  ) : (
    <>
      <DayBlock day={planned[0].day} meals={planned[0].meals} featured />
      <DayBlock day={planned[1].day} meals={planned[1].meals} />
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", columnGap: SPACE.xl, borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.xs }}>
        <Link to="/grocery" style={{ ...textAction, gap: SPACE.md }}>
          <DrawnIcon name="groceries" size={22} />
          {groceries.total === 0 ? "Groceries: nothing to buy yet" : groceries.left === 0 ? "Groceries: all done" : `Groceries: ${groceries.left} of ${groceries.total} left`}
        </Link>
        <Link to="/plan" style={textAction}>
          See the whole week
        </Link>
      </div>
    </>
  );

  const right = (
    <>
      {isAdmin && <BoxesAndMarkets householdId={mp.householdId} />}
      <NewRecipes />
    </>
  );

  return (
    <div className="mp-sans" style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? SPACE.x3 : SPACE.x4, alignItems: "flex-start" }}>
      <div style={{ flex: "3 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: SPACE.x3 }}>{left}</div>
      <div style={{ flex: "2 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: SPACE.x3 }}>{right}</div>
    </div>
  );
}

// ------------------------------------------------- today / tomorrow ---

// Today is the screen's one tinted block; Tomorrow sits on the paper.
function DayBlock({ day, meals, featured = false }: { day: HomeDay; meals: PlannedMeal[]; featured?: boolean }) {
  return (
    <section
      aria-label={day.label}
      style={featured ? { background: COLORS.accentTint, borderRadius: RADIUS.xl, padding: "var(--space-xxl)" } : { padding: "0 var(--space-hair)" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: SPACE.sm }}>
        <span style={{ ...smallLabel, color: featured ? COLORS.accent : COLORS.soft }}>{day.label}</span>
        <span style={supporting}>{formatShortDate(day.date)}</span>
      </div>
      {meals.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.xl }}>
          <span style={{ ...supporting, fontSize: FONT_SIZE.base }}>Nothing planned.</span>
          <Link to="/plan" style={textAction}>
            Plan it
          </Link>
        </div>
      ) : (
        meals.map((meal, i) => (
          <MealRow
            key={meal.slot}
            meal={meal}
            large={featured && i === 0}
            first={i === 0}
            // Today's meals open ready to cook (with the meal, so the cooking
            // layout can scale to the week and switch dishes); tomorrow's
            // open the recipe to read.
            to={meal.recipeId && (day.label === "Today" ? `/recipes/${meal.recipeId}/cook?${new URLSearchParams({ week: day.weekStart, day: day.day, slot: meal.slot })}` : `/recipes/${meal.recipeId}`)}
          />
        ))
      )}
    </section>
  );
}

function MealRow({ meal, large, first, to }: { meal: PlannedMeal; large: boolean; first: boolean; to: string | null }) {
  const title = meal.isLeftover ? `Leftovers: ${meal.title}` : meal.title;
  const meta = [meal.sourceLabel, meal.minutes > 0 ? formatMinutes(meal.minutes) : "", meal.withTitles.length ? `with ${meal.withTitles.join(", ")}` : ""].filter(Boolean).join(" · ");
  const body: ReactNode = (
    <>
      <span style={supporting}>{SLOT_LABELS[meal.slot]}</span>
      <span style={{ fontFamily: "var(--font-serif)", fontSize: large ? FONT_SIZE.feature : FONT_SIZE.h4, fontWeight: 600, lineHeight: 1.15, color: COLORS.ink }}>{title}</span>
      {meta && <span style={supporting}>{meta}</span>}
    </>
  );
  const rowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: SPACE.hair,
    padding: "var(--space-lg) 0",
    borderTop: first ? "none" : `1px solid ${COLORS.line}`,
    textDecoration: "none",
  };
  return to ? (
    <Link to={to} style={rowStyle}>
      {body}
    </Link>
  ) : (
    <div style={rowStyle}>{body}</div>
  );
}

// Wireframe 13: nothing in this week at all.
function EmptyWeek({ date, canCopyLastWeek, onCopyLastWeek }: { date: Date; canCopyLastWeek: boolean; onCopyLastWeek: () => void }) {
  return (
    <section aria-label="Today and tomorrow" style={{ background: COLORS.accentTint, borderRadius: RADIUS.xl, padding: "var(--space-xxl)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: SPACE.md }}>
        <span style={{ ...smallLabel, color: COLORS.accent }}>Today and tomorrow</span>
        <span style={supporting}>{formatShortDate(date)}</span>
      </div>
      <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600, color: COLORS.ink }}>Nothing is planned yet</div>
      <p style={{ margin: "var(--space-xs) 0 var(--space-xxl)", fontSize: FONT_SIZE.base, color: COLORS.ink }}>Pick a few dinners and the grocery list builds itself.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
        <Link to="/plan" style={primaryButton}>
          Plan this week
        </Link>
        {canCopyLastWeek && (
          <button type="button" className="mp-btn" onClick={onCopyLastWeek} style={secondaryButton}>
            Start from last week
          </button>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------ boxes and markets ---

type SourcesState = { status: "loading" } | { status: "error" } | { status: "ready"; purveyors: Purveyor[]; sourceIds: string[] };

function BoxesAndMarkets({ householdId }: { householdId: string }) {
  const [state, setState] = useState<SourcesState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadPurveyorDirectory(), loadHouseholdSources(householdId)])
      .then(([purveyors, sourceIds]) => !cancelled && setState({ status: "ready", purveyors: purveyors as Purveyor[], sourceIds: sourceIds as string[] }))
      .catch(() => !cancelled && setState({ status: "error" }));
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  let content: ReactNode;
  if (state.status === "loading") {
    content = <p style={{ ...supporting, margin: 0 }}>Loading…</p>;
  } else if (state.status === "error") {
    content = <p style={{ ...supporting, margin: 0 }}>Couldn't load your sources just now.</p>;
  } else if (state.sourceIds.length === 0) {
    content = (
      <>
        <p style={{ margin: 0, fontSize: FONT_SIZE.base }}>Star the farms, CSAs and markets you buy from to see their boxes and market days here.</p>
        <Link to="/purveyors" style={textAction}>
          Find your sources
        </Link>
      </>
    );
  } else {
    const now = new Date();
    const boxes = upcomingBoxes(state.purveyors, state.sourceIds, toISODate(now));
    const markets = upcomingMarketDays(state.purveyors, state.sourceIds, now);
    content =
      boxes.length + markets.length === 0 ? (
        <p style={{ ...supporting, margin: 0 }}>Nothing coming up from your sources in the next two weeks.</p>
      ) : (
        <div>
          {boxes.map(({ purveyor, box }) => {
            const count = box.items.length;
            const meta = [`Arrives ${formatShortDate(isoToDate(box.deliveryDate))}`, box.status === "Confirmed" ? `contents confirmed, ${count} item${count === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ");
            return <SourceRow key={box.id} icon="crate" title={box.name || `Box from ${purveyor.name}`} meta={meta} />;
          })}
          {markets.map(({ purveyor, date, hours, location }) => {
            const time = [formatTime(hours.openTime), formatTime(hours.closeTime)].filter(Boolean).join(" to ");
            const place = location ? [location.address, location.city].filter(Boolean).join(", ") : "";
            return <SourceRow key={purveyor.id} icon="stall" title={purveyor.name} meta={[formatShortDate(date), time, place].filter(Boolean).join(" · ")} />;
          })}
        </div>
      );
  }

  return (
    <section aria-labelledby="home-sources">
      <h2 id="home-sources" style={sectionHeading}>
        Boxes and markets
      </h2>
      {content}
    </section>
  );
}

function SourceRow({ icon, title, meta }: { icon: "crate" | "stall"; title: string; meta: string }) {
  return (
    <div style={{ display: "flex", gap: SPACE.xl, alignItems: "flex-start", padding: "var(--space-lg) 0", borderBottom: `1px solid ${COLORS.line}` }}>
      <span style={{ color: COLORS.ink, paddingTop: SPACE.hair }}>
        <DrawnIcon name={icon} size={28} />
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.hair, minWidth: 0 }}>
        <span style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600, lineHeight: 1.2 }}>{title}</span>
        {meta && <span style={supporting}>{meta}</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------- new recipes ---

function NewRecipes() {
  const { recipes } = useMealPlanner();
  const newest = useMemo(() => newestRecipes(recipes), [recipes]);

  return (
    <section aria-labelledby="home-new-recipes">
      <h2 id="home-new-recipes" style={sectionHeading}>
        New recipes
      </h2>
      {newest.length === 0 ? (
        <>
          <p style={{ margin: 0, fontSize: FONT_SIZE.base }}>Recipes you add or that are shared with you show up here.</p>
          <Link to="/recipes" style={textAction}>
            Add a recipe
          </Link>
        </>
      ) : (
        <div>
          {newest.map((r) => {
            const minutes = (r.activeTime || 0) + (r.cookTime || 0);
            const meta = [minutes > 0 ? formatMinutes(minutes) : "", r.servings ? `serves ${r.servings}` : ""].filter(Boolean).join(" · ");
            return (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.xl, padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}` }}>
                <Link to={`/recipes/${r.id}`} style={{ display: "flex", flexDirection: "column", gap: SPACE.hair, minWidth: 0, textDecoration: "none" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600, lineHeight: 1.2, color: COLORS.ink }}>{r.title}</span>
                  {meta && <span style={supporting}>{meta}</span>}
                </Link>
                {/* The plan picker comes with slice 5; until then the
                    recipe page's own "Add to plan" does the job. */}
                <Link to={`/recipes/${r.id}`} style={{ ...textAction, flexShrink: 0 }}>
                  Add to plan
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
